/**
 * captureContext — Auto-collected metadata for a tester note.
 *
 * Pure-function counterpart to `frontend/apps/ops-mobile/src/lib/dev-notes-context.ts`.
 * Mirrors the shape so the same backend column set is populated.
 *
 * @package @plerity/tester-notes-widget
 */

import type { CapturedContext, DeviceType } from "./types";

// Vite/build-time defines — added by `createDashboardConfig` extensions or
// directly in app vite configs. All optional. Cast through `unknown` so this
// file doesn't have to know about every shell's build define union.
declare const __DEPLOY_SHA__: string | undefined;

const SESSION_KEY = "__tester_notes_session_id";
const PRIOR_PATH_KEY = "__tester_notes_prior_path";

/** Cheap, collision-resistant ID using base36 timestamp + random uint32. */
export function generateLocalId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.floor(Math.random() * 0xffff_ffff).toString(36);
  return `web-${ts}-${rand}`;
}

/** Per-cold-start session ID, persisted only in window so refresh resets it. */
export function getSessionId(): string {
  if (typeof window === "undefined") return "web-no-window";
  const w = window as Window & { [SESSION_KEY]?: string };
  if (w[SESSION_KEY]) return w[SESSION_KEY] as string;
  const id = generateLocalId().replace(/^web-/, "sess-");
  w[SESSION_KEY] = id;
  return id;
}

/**
 * Track the current path so the next `captureWebContext()` call can reference
 * it as `prior_page_path`. Call once per route change in your app.
 *
 * The shell wrapper (createShell → ShellTesterNotesWidget) wires this up
 * automatically via React Router's useLocation effect — apps don't need to
 * call it directly.
 */
export function trackNavigation(pathname: string): void {
  if (typeof window === "undefined") return;
  const w = window as Window & {
    [SESSION_KEY]?: string;
    [PRIOR_PATH_KEY]?: { current: string; previous: string | null };
  };
  const slot = w[PRIOR_PATH_KEY] ?? { current: "", previous: null };
  if (slot.current === pathname) return; // no-op if same route
  slot.previous = slot.current || null;
  slot.current = pathname;
  w[PRIOR_PATH_KEY] = slot;
}

function getPriorPath(): string | null {
  if (typeof window === "undefined") return null;
  const slot = (
    window as Window & {
      [PRIOR_PATH_KEY]?: { current: string; previous: string | null };
    }
  )[PRIOR_PATH_KEY];
  return slot?.previous ?? null;
}

/** Detect device type from user agent + width. */
export function detectDeviceType(): DeviceType {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  if (/iPad|Tablet/i.test(ua) || (window.innerWidth >= 600 && window.innerWidth <= 1024)) {
    return "tablet";
  }
  return "desktop";
}

/**
 * Derive a stable `section_type` slug from the pathname — the first
 * non-empty segment, lowercased and `-`-normalized. Mirrors the mobile
 * `resolveRouteContext` output ("messaging-channel", "more-index"), but
 * web shells use simpler 1-segment route groups (`/settings/profile` →
 * `"settings"`). Empty path / root → `"home"`.
 *
 * Backend stores this in `section_type VARCHAR(255)` — keeping it a slug
 * (not a free-form label) makes filtering / grouping notes by route
 * group predictable in the dev-notes admin UI.
 */
export function pagePathToSectionType(pathname: string): string {
  if (!pathname || pathname === "/") return "home";
  const first = pathname.split("/").filter(Boolean)[0];
  if (!first) return "home";
  return first.toLowerCase().replace(/[^a-z0-9-]/g, "-");
}

/** Convert a path like `/finance/bookkeeping/overview` to "Finance > Bookkeeping > Overview". */
export function pagePathToName(pathname: string): string {
  if (!pathname || pathname === "/") return "Home";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "Home";
  return segments
    .map((seg) => seg.replace(/[-_]/g, " "))
    .map((seg) => seg.replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" > ");
}

/** Derive the app version from a Vite env var if the build wired it up. */
function getAppVersion(): string | null {
  // Vite-only: import.meta.env is statically replaced. Node/Vitest also handles it.
  try {
    // Vite's client types aren't guaranteed to be referenced by every consumer
    // of this package, so `import.meta.env` isn't statically typed here.
    const env = (import.meta as unknown as { env?: Record<string, unknown> })?.env;
    if (env && typeof env.VITE_APP_VERSION === "string") return env.VITE_APP_VERSION;
  } catch {
    // ignore
  }
  return null;
}

function getNetworkState(): "online" | "offline" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  if (typeof navigator.onLine !== "boolean") return "unknown";
  return navigator.onLine ? "online" : "offline";
}

function getLocale(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale || "en-US";
  } catch {
    return "en-US";
  }
}

/**
 * Best-effort browser identifier — `"Chrome 134 on macOS"`. Mirrors the
 * operational role of mobile's `device_model` field ("iPhone 16 Pro").
 * Uses UA-CH high-entropy values when available (Chromium 90+) and falls
 * back to a simple navigator.userAgent regex.
 */
export function detectDeviceModel(): string | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // Browser
  let browser: string | null = null;
  if (/Edg\//.test(ua)) browser = `Edge ${(ua.match(/Edg\/(\d+)/) ?? [])[1] ?? ""}`.trim();
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua))
    browser = `Chrome ${(ua.match(/Chrome\/(\d+)/) ?? [])[1] ?? ""}`.trim();
  else if (/Firefox\//.test(ua)) browser = `Firefox ${(ua.match(/Firefox\/(\d+)/) ?? [])[1] ?? ""}`.trim();
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua))
    browser = `Safari ${(ua.match(/Version\/(\d+)/) ?? [])[1] ?? ""}`.trim();
  // Platform — order matters: iOS UAs include "Mac OS X" too, so iPhone/iPad
  // must be detected first; Android UAs include "Linux" so it must come before
  // the Linux check.
  let platform: string | null = null;
  if (/iPhone|iPad|iPod/.test(ua)) platform = "iOS";
  else if (/Android/.test(ua)) platform = "Android";
  else if (/Macintosh|Mac OS X/.test(ua)) platform = "macOS";
  else if (/Windows NT/.test(ua)) platform = "Windows";
  else if (/Linux/.test(ua)) platform = "Linux";

  if (browser && platform) return `${browser} on ${platform}`;
  return browser || platform || null;
}

/**
 * OS string — `"macOS 14.5"` when discoverable, else just the platform
 * family. Matches mobile's `os_version` shape.
 */
export function detectOsVersion(): string | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // macOS version is in "Mac OS X 10_15_7" form
  const macMatch = ua.match(/Mac OS X (\d+[_.]\d+(?:[_.]\d+)?)/);
  if (macMatch) return `macOS ${macMatch[1].replace(/_/g, ".")}`;
  const winMatch = ua.match(/Windows NT (\d+(?:\.\d+)?)/);
  if (winMatch) return `Windows NT ${winMatch[1]}`;
  const iosMatch = ua.match(/(?:iPhone|iPad).+OS (\d+[_.]\d+(?:[_.]\d+)?)/);
  if (iosMatch) return `iOS ${iosMatch[1].replace(/_/g, ".")}`;
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
  if (androidMatch) return `Android ${androidMatch[1]}`;
  // Last-resort: identify just the family.
  if (/Linux/.test(ua)) return "Linux";
  return null;
}

/**
 * Map the current hostname to a deploy-environment slug. Used by the
 * dev-notes admin UI to filter "prod-only" vs "preview" reports.
 *
 *   ops.animasmarketing.com           → 'production'
 *   preview-ops.animasmarketing.com   → 'preview'
 *   ops-<hash>.pages.dev              → 'pages-dev'
 *   localhost / 127.0.0.1             → 'development'
 *   anything else                     → null (unknown — don't lie)
 */
export function detectDeployEnvironment(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname || "";
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost")) {
    return "development";
  }
  if (host.endsWith(".pages.dev")) return "pages-dev";
  if (host.startsWith("preview-") && host.endsWith(".animasmarketing.com")) return "preview";
  if (host.endsWith(".animasmarketing.com")) return "production";
  return null;
}

/**
 * Git SHA of the current build — populated by a Vite `define` block at
 * build time (e.g. `__DEPLOY_SHA__: JSON.stringify(execSync('git rev-parse HEAD'))`).
 * Returns null when the host app didn't wire it up; never throws.
 */
export function getDeploySha(): string | null {
  try {
    // typeof guard avoids ReferenceError when the define wasn't set
    if (typeof __DEPLOY_SHA__ !== "undefined" && __DEPLOY_SHA__) {
      return __DEPLOY_SHA__.slice(0, 12);
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Snapshot all auto-captured fields used by a tester note. Pure: no side
 * effects (you must call `trackNavigation` separately on route change).
 *
 * @param overrideRoute — optional pathname; falls back to window.location.pathname
 */
export function captureWebContext(overrideRoute?: string): CapturedContext {
  const path = overrideRoute ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = typeof window !== "undefined" ? window.location.href : "";
  return {
    page_path: path,
    page_url: url,
    page_name: pagePathToName(path),
    section_type: pagePathToSectionType(path),
    device_type: detectDeviceType(),
    network_state: getNetworkState(),
    session_id: getSessionId(),
    prior_page_path: getPriorPath(),
    app_version: getAppVersion(),
    device_model: detectDeviceModel(),
    os_version: detectOsVersion(),
    deploy_environment: detectDeployEnvironment(),
    deploy_sha: getDeploySha(),
    locale: getLocale(),
    captured_at: new Date().toISOString(),
  };
}
